import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { 
    X, 
    Filter, 
    Calendar, 
    Users, 
    Clock, 
    Edit, 
    Trash2, 
    Plus,
    ChevronLeft,
    ChevronRight,
    CheckSquare,
    Square,
    MoreVertical,
    Download
} from 'lucide-react';
import AddRecurrenceModal from './AddRecurrenceModal';
import EditRecurrenceModal from './EditRecurrenceModal';
import BulkEditModal from './BulkEditModal';
import EditCourseModal from './EditCourseModal';

export default function RecurrenceManager({ isOpen, course, onClose, onRecurrenceUpdated }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    const [loading, setLoading] = useState(true);
    const [recurrences, setRecurrences] = useState([]);
    const [selectedRecurrences, setSelectedRecurrences] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage] = useState(10);
    
    // Filtri
    const [filters, setFilters] = useState(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth() + 1; // +1 perché getMonth() è 0-based
        return {
            month: `${year}-${String(month).padStart(2, '0')}`, // YYYY-MM
        onlyFuture: true,
        status: 'all' // all, active, cancelled
        };
    });
    
    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [selectedRecurrence, setSelectedRecurrence] = useState(null);
    const [courseTab, setCourseTab] = useState('dates');

    useEffect(() => {
        if (isOpen && course) {
            fetchRecurrences();
        }
    }, [isOpen, course, currentPage, filters]);

    const fetchRecurrences = async ({ silent = false } = {}) => {
        try {
            if (!silent) {
                setLoading(true);
            }
            const api = getAuthenticatedApi();
            
            // Usa l'endpoint esistente che funziona
            const month = filters.month || (() => {
                const today = new Date();
                const year = today.getFullYear();
                const monthNum = today.getMonth() + 1;
                return `${year}-${String(monthNum).padStart(2, '0')}`;
            })();
            const response = await api.get(`/gym_courses.php?action=list_sessions&month=${month}`);
            
            // Filtra solo le sessioni di questo corso
            let courseSessions = (response.data.sessions || []).filter(s => s.course_id === course.id);
            
            // Applica filtri aggiuntivi
            if (filters.search) {
                const searchTerm = filters.search.toLowerCase();
                courseSessions = courseSessions.filter(s => 
                    s.title?.toLowerCase().includes(searchTerm) ||
                    s.description?.toLowerCase().includes(searchTerm)
                );
            }
            
            if (filters.onlyFuture) {
                // Confronta solo le date (senza ora) per includere anche oggi
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Normalizza a mezzanotte
                courseSessions = courseSessions.filter(s => {
                    const sessionDate = new Date(s.session_date);
                    sessionDate.setHours(0, 0, 0, 0); // Normalizza a mezzanotte
                    return sessionDate >= today;
                });
            }
            
            if (filters.status && filters.status !== 'all') {
                courseSessions = courseSessions.filter(s => s.status === filters.status);
            }
            
            // Ordina le sessioni per data crescente (prima le più vicine)
            courseSessions.sort((a, b) => {
                const dateA = new Date(a.session_date);
                const dateB = new Date(b.session_date);
                return dateA - dateB;
            });
            
            // Paginazione manuale
            const total = courseSessions.length;
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedSessions = courseSessions.slice(startIndex, endIndex);
            
            setRecurrences(paginatedSessions);
            setTotalPages(Math.ceil(total / itemsPerPage));
            
        } catch (err) {
            console.error('Errore caricamento lezioni:', err);
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    const handleSelectAll = () => {
        if (selectedRecurrences.length === recurrences.length) {
            setSelectedRecurrences([]);
        } else {
            setSelectedRecurrences(recurrences.map(r => r.id));
        }
    };

    const handleSelectRecurrence = (recurrenceId) => {
        setSelectedRecurrences(prev => 
            prev.includes(recurrenceId)
                ? prev.filter(id => id !== recurrenceId)
                : [...prev, recurrenceId]
        );
    };

    const handleBulkDelete = async () => {
        if (selectedRecurrences.length === 0) return;
        
        if (!window.confirm(t('gym.courses.recurrenceManager.messages.deleteConfirm', { count: selectedRecurrences.length }))) {
            return;
        }

        try {
            const api = getAuthenticatedApi();
            
            // Elimina una per una usando l'endpoint esistente
            for (const recurrenceId of selectedRecurrences) {
                await api.delete(`/gym_courses.php?action=delete_session&id=${recurrenceId}`);
            }
            
            setSelectedRecurrences([]);
            fetchRecurrences({ silent: true });
            onRecurrenceUpdated();
            
        } catch (err) {
            console.error('Errore eliminazione lezioni:', err);
            toast.error('Errore durante l\'eliminazione delle lezioni');
        }
    };

    const handleBulkEdit = () => {
        if (selectedRecurrences.length === 0) return;
        setShowBulkModal(true);
    };

    const handleEditRecurrence = (recurrence) => {
        setSelectedRecurrence(recurrence);
        setShowEditModal(true);
    };

    const handleDeleteRecurrence = async (recurrenceId) => {
        if (!window.confirm(t('gym.courses.recurrenceManager.messages.deleteSingleConfirm'))) return;

        try {
            const api = getAuthenticatedApi();
            await api.delete(`/gym_courses.php?action=delete_session&id=${recurrenceId}`);
            
            fetchRecurrences({ silent: true });
            onRecurrenceUpdated();
            
        } catch (err) {
            console.error('Errore eliminazione lezione:', err);
            toast.error('Errore durante l\'eliminazione');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('it-IT', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (timeString) => {
        return timeString?.slice(0, 5) || '';
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-800 dark:text-green-400', label: t('gym.courses.recurrenceManager.status.active') },
            cancelled: { bg: 'bg-red-100 dark:bg-red-900/20', text: 'text-red-800 dark:text-red-400', label: t('gym.courses.recurrenceManager.status.cancelled') },
            completed: { bg: 'bg-gray-100 dark:bg-gray-900/20', text: 'text-gray-800 dark:text-white/65', label: t('gym.courses.recurrenceManager.status.completed') }
        };
        
        const badge = badges[status] || badges.active;
        
        return (
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badge.bg} ${badge.text}`}>
                {badge.label}
            </span>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-6xl my-8">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {t('gym.courses.recurrenceManager.title')}: {course.title}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-white/65 mt-1">
                            {t('gym.courses.recurrenceManager.subtitle', { 
                                count: recurrences.length, 
                                current: currentPage, 
                                total: totalPages 
                            })}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex border-b border-gray-200 px-6 dark:border-white/10">
                    <button
                        type="button"
                        onClick={() => setCourseTab('edit')}
                        className={`border-b-2 px-4 py-3 text-sm font-medium ${
                            courseTab === 'edit'
                                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                                : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-white/60 dark:hover:text-white'
                        }`}
                    >
                        Modifica
                    </button>
                    <button
                        type="button"
                        onClick={() => setCourseTab('dates')}
                        className={`border-b-2 px-4 py-3 text-sm font-medium ${
                            courseTab === 'dates'
                                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                                : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-white/60 dark:hover:text-white'
                        }`}
                    >
                        Ricorrenze
                    </button>
                </div>

                <div className="p-6">
                    {courseTab === 'edit' && (
                        <EditCourseModal
                            isOpen
                            embedded
                            course={course}
                            onClose={() => {}}
                            onSuccess={onRecurrenceUpdated}
                        />
                    )}
                    {courseTab === 'dates' && (
                    <>
                    {/* Filtri e Azioni */}
                    <div className="flex flex-col lg:flex-row gap-4 mb-6">
                        {/* Filtri */}
                        <div className="flex flex-wrap gap-3 flex-1">
                            {/* Selettore Mese Migliorato */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        const [year, month] = filters.month.split('-').map(Number);
                                        const prevMonth = month === 1 ? 12 : month - 1;
                                        const prevYear = month === 1 ? year - 1 : year;
                                        setFilters(prev => ({ 
                                            ...prev, 
                                            month: `${prevYear}-${String(prevMonth).padStart(2, '0')}`
                                        }));
                                    }}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                                    title="Mese precedente"
                                >
                                    <ChevronLeft size={20} className="text-gray-600 dark:text-white/65" />
                                </button>
                                
                                <select
                                value={filters.month}
                                onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-medium min-w-[180px]"
                                >
                                    {(() => {
                                        const options = [];
                                        const today = new Date();
                                        const currentYear = today.getFullYear();
                                        const currentMonth = today.getMonth();
                                        
                                        // Genera opzioni per 6 mesi passati e 12 mesi futuri
                                        for (let i = -6; i <= 12; i++) {
                                            const targetDate = new Date(currentYear, currentMonth + i, 1);
                                            const year = targetDate.getFullYear();
                                            const month = targetDate.getMonth() + 1; // +1 perché getMonth() è 0-based
                                            
                                            // Costruisci value manualmente senza conversione UTC
                                            const value = `${year}-${String(month).padStart(2, '0')}`;
                                            
                                            const label = targetDate.toLocaleDateString('it-IT', { 
                                                year: 'numeric', 
                                                month: 'long' 
                                            });
                                            const isCurrentMonth = i === 0;
                                            options.push(
                                                <option key={value} value={value}>
                                                    {isCurrentMonth ? `📍 ${label}` : label}
                                                </option>
                                            );
                                        }
                                        return options;
                                    })()}
                                </select>
                                
                                <button
                                    onClick={() => {
                                        const [year, month] = filters.month.split('-').map(Number);
                                        const nextMonth = month === 12 ? 1 : month + 1;
                                        const nextYear = month === 12 ? year + 1 : year;
                                        setFilters(prev => ({ 
                                            ...prev, 
                                            month: `${nextYear}-${String(nextMonth).padStart(2, '0')}`
                                        }));
                                    }}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                                    title="Mese successivo"
                                >
                                    <ChevronRight size={20} className="text-gray-600 dark:text-white/65" />
                                </button>
                                
                                <button
                                    onClick={() => {
                                        const today = new Date();
                                        const year = today.getFullYear();
                                        const month = today.getMonth() + 1;
                                        setFilters(prev => ({ 
                                            ...prev, 
                                            month: `${year}-${String(month).padStart(2, '0')}`
                                        }));
                                    }}
                                    className="px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                                    title="Torna al mese corrente"
                                >
                                    Oggi
                                </button>
                            </div>
                            
                            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-white/85">
                                <input
                                    type="checkbox"
                                    checked={filters.onlyFuture}
                                    onChange={(e) => setFilters(prev => ({ ...prev, onlyFuture: e.target.checked }))}
                                    className="w-4 h-4 text-indigo-600 rounded"
                                />
                                {t('gym.courses.recurrenceManager.filters.onlyFuture')}
                            </label>
                        </div>

                        {/* Azioni */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm"
                            >
                                <Plus size={16} />
                                {t('gym.courses.recurrenceManager.actions.add')}
                            </button>
                            
                            {selectedRecurrences.length > 0 && (
                                <>
                                    <button
                                        onClick={handleBulkEdit}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                                    >
                                        <Edit size={16} />
                                        {t('gym.courses.recurrenceManager.actions.bulkEdit', { count: selectedRecurrences.length })}
                                    </button>
                                    
                                    <button
                                        onClick={handleBulkDelete}
                                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
                                    >
                                        <Trash2 size={16} />
                                        {t('gym.courses.recurrenceManager.actions.bulkDelete', { count: selectedRecurrences.length })}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Tabella */}
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-white/10">
                                        <th className="text-left py-3 px-4">
                                            <button
                                                onClick={handleSelectAll}
                                                className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-white/85"
                                            >
                                                {selectedRecurrences.length === recurrences.length ? (
                                                    <CheckSquare size={16} className="text-indigo-600" />
                                                ) : (
                                                    <Square size={16} />
                                                )}
                                                {t('gym.courses.recurrenceManager.actions.selectAll')}
                                            </button>
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-white/85">
                                            {t('gym.courses.recurrenceManager.table.headers.date')}
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-white/85">
                                            {t('gym.courses.recurrenceManager.table.headers.time')}
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-white/85">
                                            {t('gym.courses.recurrenceManager.table.headers.participants')}
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-white/85">
                                            {t('gym.courses.recurrenceManager.table.headers.status')}
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-white/85">
                                            {t('gym.courses.recurrenceManager.table.headers.actions')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recurrences.map((recurrence) => (
                                        <tr
                                            key={recurrence.id}
                                            onClick={() => handleEditRecurrence(recurrence)}
                                            className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/[0.06]"
                                        >
                                            <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => handleSelectRecurrence(recurrence.id)}
                                                    className="flex items-center gap-2"
                                                >
                                                    {selectedRecurrences.includes(recurrence.id) ? (
                                                        <CheckSquare size={16} className="text-indigo-600" />
                                                    ) : (
                                                        <Square size={16} className="text-gray-400" />
                                                    )}
                                                </button>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <Calendar size={16} className="text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {formatDate(recurrence.session_date)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <Clock size={16} className="text-gray-400" />
                                                    <span className="text-sm text-gray-900 dark:text-white">
                                                        {formatTime(recurrence.start_time)} - {formatTime(recurrence.end_time)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <Users size={16} className="text-gray-400" />
                                                    <span className="text-sm text-gray-900 dark:text-white">
                                                        {recurrence.current_participants || 0} / {recurrence.max_participants || '∞'}
                                                    </span>
                                                    {Number(recurrence.waitlist_count) > 0 && (
                                                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                                            {recurrence.waitlist_count} in lista
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                {getStatusBadge(recurrence.status)}
                                            </td>
                                            <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleEditRecurrence(recurrence)}
                                                        className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                                        title="Modifica"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteRecurrence(recurrence.id)}
                                                        className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                                        title="Elimina"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Paginazione */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6">
                            <div className="text-sm text-gray-500 dark:text-white/65">
                                {t('gym.courses.recurrenceManager.table.pagination.showing', {
                                    start: ((currentPage - 1) * itemsPerPage) + 1,
                                    end: Math.min(currentPage * itemsPerPage, recurrences.length),
                                    total: recurrences.length
                                })}
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                
                                <span className="px-3 py-1 text-sm text-gray-700 dark:text-white/85">
                                    {currentPage} di {totalPages}
                                </span>
                                
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                    </>
                    )}
                </div>
            </div>

            {/* Modal Aggiungi Lezioni */}
            {showAddModal && (
                <AddRecurrenceModal
                    isOpen={showAddModal}
                    course={course}
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => {
                        setShowAddModal(false);
                        fetchRecurrences({ silent: true });
                        onRecurrenceUpdated();
                    }}
                />
            )}

            {/* Modal Modifica Lezione */}
            {showEditModal && selectedRecurrence && (
                <EditRecurrenceModal
                    isOpen={showEditModal}
                    recurrence={selectedRecurrence}
                    course={course}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedRecurrence(null);
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedRecurrence(null);
                        fetchRecurrences({ silent: true });
                        onRecurrenceUpdated();
                    }}
                />
            )}

            {/* Modal Modifica Multipla */}
            {showBulkModal && (
                <BulkEditModal
                    isOpen={showBulkModal}
                    selectedRecurrences={selectedRecurrences}
                    onClose={() => {
                        setShowBulkModal(false);
                        setSelectedRecurrences([]);
                    }}
                    onSuccess={() => {
                        setShowBulkModal(false);
                        setSelectedRecurrences([]);
                        fetchRecurrences({ silent: true });
                        onRecurrenceUpdated();
                    }}
                />
            )}
        </div>
    );
}
